import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Loader2, Plus, X, ArrowLeft, Users2, Copy, Archive, ArchiveRestore, Trash2, Download,
  Search, UserPlus, UserMinus, History, LayoutDashboard, ClipboardList, Pencil, Save,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { tInput, tLabel, tBtn, tBtnGhost } from "./trackUi";

const EMPTY_FORM = {
  name: "", description: "", sport: "", event_category: "", level: "", location: "",
  training_days: "", start_date: "", end_date: "", capacity: "", coach_ids: [],
};

function GroupForm({ orgId, initial, coaches, onCancel, onSaved }) {
  const editing = !!initial?.id;
  const [form, setForm] = useState(() => ({
    ...EMPTY_FORM, ...initial,
    capacity: initial?.capacity ?? "", coach_ids: initial?.coach_ids || [],
  }));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleCoach = (id) =>
    setForm((f) => ({ ...f, coach_ids: f.coach_ids.includes(id) ? f.coach_ids.filter((c) => c !== id) : [...f.coach_ids, id] }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      name: form.name, description: form.description || null, sport: form.sport || null,
      event_category: form.event_category || null, level: form.level || null, location: form.location || null,
      training_days: form.training_days || null, start_date: form.start_date || null, end_date: form.end_date || null,
      capacity: form.capacity === "" ? null : Number(form.capacity), coach_ids: form.coach_ids,
    };
    try {
      if (editing) await api.patch(`/track/groups/${initial.id}`, payload);
      else await api.post("/track/groups", { ...payload, organization_id: orgId });
      toast.success(editing ? "Group updated." : "Group created.");
      onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="bg-[#121212] border border-white/10 p-6 mb-6 space-y-4" data-testid="group-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className={tLabel}>Group Name *</label><input data-testid="group-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className={tInput} placeholder="Sprint Squad" /></div>
        <div><label className={tLabel}>Level</label><input data-testid="group-level" value={form.level || ""} onChange={(e) => set("level", e.target.value)} className={tInput} placeholder="Varsity / JV" /></div>
        <div><label className={tLabel}>Sport / Discipline</label><input value={form.sport || ""} onChange={(e) => set("sport", e.target.value)} className={tInput} placeholder="Track & Field" /></div>
        <div><label className={tLabel}>Event Category</label><input value={form.event_category || ""} onChange={(e) => set("event_category", e.target.value)} className={tInput} placeholder="Sprints / Jumps" /></div>
        <div><label className={tLabel}>Location</label><input value={form.location || ""} onChange={(e) => set("location", e.target.value)} className={tInput} /></div>
        <div><label className={tLabel}>Training Days</label><input value={form.training_days || ""} onChange={(e) => set("training_days", e.target.value)} className={tInput} placeholder="Mon/Wed/Fri" /></div>
        <div><label className={tLabel}>Start Date</label><input type="date" value={form.start_date || ""} onChange={(e) => set("start_date", e.target.value)} className={tInput} /></div>
        <div><label className={tLabel}>End Date</label><input type="date" value={form.end_date || ""} onChange={(e) => set("end_date", e.target.value)} className={tInput} /></div>
        <div><label className={tLabel}>Capacity</label><input type="number" min="0" value={form.capacity} onChange={(e) => set("capacity", e.target.value)} className={tInput} /></div>
      </div>
      <div><label className={tLabel}>Description</label><textarea value={form.description || ""} onChange={(e) => set("description", e.target.value)} className={`${tInput} min-h-[70px]`} /></div>
      {coaches.length > 0 && (
        <div>
          <label className={tLabel}>Assigned Coaches</label>
          <div className="flex flex-wrap gap-1.5" data-testid="group-coach-picker">
            {coaches.map((c) => (
              <button type="button" key={c.id} onClick={() => toggleCoach(c.id)}
                className={`px-2.5 py-1 text-xs border transition-colors ${form.coach_ids.includes(c.id) ? "bg-white text-black border-white" : "border-white/15 text-white/50 hover:border-white/40"}`}>
                {c.name}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="flex gap-2">
        <button data-testid="group-submit" disabled={saving} className={tBtn}>{saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {editing ? "Save Changes" : "Create Group"}</button>
        <button type="button" onClick={onCancel} className={tBtnGhost}><X size={14} /> Cancel</button>
      </div>
    </form>
  );
}

function GroupsList({ orgId, canWrite, coaches, onOpen }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("active");
  const [q, setQ] = useState("");
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (orgId) params.set("organization_id", orgId);
      if (statusFilter !== "all") params.set("status", statusFilter);
      const { data } = await api.get(`/track/groups?${params.toString()}`);
      setList(data);
    } catch { toast.error("Failed to load groups."); } finally { setLoading(false); }
  }, [orgId, statusFilter]);
  useEffect(() => { load(); }, [load]);

  const filtered = list.filter((g) => g.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="track-groups-list">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div><h1 className="font-display font-black uppercase tracking-tighter text-4xl">Athlete Groups</h1><p className="text-white/40 text-sm mt-1" data-testid="groups-count">{filtered.length} group{filtered.length === 1 ? "" : "s"}</p></div>
        {canWrite && <button data-testid="add-group-btn" onClick={() => setShowForm((v) => !v)} className={tBtn}>{showForm ? <X size={14} /> : <Plus size={14} />} {showForm ? "Cancel" : "New Group"}</button>}
      </div>

      {showForm && canWrite && <GroupForm orgId={orgId} coaches={coaches} onCancel={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}

      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input data-testid="groups-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search groups…" className={`${tInput} pl-9`} />
        </div>
        <div className="flex gap-1.5" data-testid="groups-status-filter">
          {["active", "archived", "all"].map((s) => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-3 py-2 text-xs uppercase tracking-widest font-bold border transition-colors ${statusFilter === s ? "bg-white text-black border-white" : "border-white/15 text-white/50 hover:border-white/40"}`}>{s}</button>
          ))}
        </div>
      </div>

      {loading ? <div className="flex justify-center py-16 text-white/40"><Loader2 className="animate-spin" /></div>
        : filtered.length === 0 ? <div className="border border-white/10 bg-[#121212] p-6 text-white/40 text-sm" data-testid="groups-empty">No groups found. {canWrite ? "Create one to organize your athletes." : ""}</div>
        : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((g) => (
              <button key={g.id} data-testid={`group-card-${g.id}`} onClick={() => onOpen(g.id)}
                className="text-left bg-[#121212] border border-white/10 hover:border-white/30 transition-colors p-5">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-display font-black uppercase tracking-tight text-xl leading-tight">{g.name}</p>
                  {g.status === "archived" && <span className="font-mono-accent text-[9px] tracking-[0.2em] uppercase border border-white/15 px-2 py-0.5 text-white/50">Archived</span>}
                </div>
                {g.level && <p className="text-white/40 text-xs mt-1">{g.level}{g.sport ? ` · ${g.sport}` : ""}</p>}
                <div className="flex items-center gap-2 mt-4 text-white/60"><Users2 size={15} /><span className="font-bold text-sm" data-testid={`group-member-count-${g.id}`}>{g.member_count} athlete{g.member_count === 1 ? "" : "s"}</span></div>
                {g.training_days && <p className="text-white/30 text-xs mt-2">{g.training_days}</p>}
              </button>
            ))}
          </div>
        )}
    </div>
  );
}

const HISTORY_LABELS = {
  group_created: "Group created", group_renamed: "Group renamed", group_archived: "Group archived",
  group_active: "Group restored", athlete_added: "Athlete added", athlete_removed: "Athlete removed",
};

function AddMembersPanel({ orgId, groupId, currentIds, onDone, onClose }) {
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState([]);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const p = orgId ? `?organization_id=${orgId}` : "";
    api.get(`/track/athletes${p}`).then(({ data }) => setAthletes(data)).catch(() => {}).finally(() => setLoading(false));
  }, [orgId]);

  const available = athletes.filter((a) => !currentIds.includes(a.id) && a.name.toLowerCase().includes(q.toLowerCase()));
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const add = async () => {
    if (!sel.length) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/track/groups/${groupId}/members`, { athlete_ids: sel });
      toast.success(`Added ${data.added} athlete(s).`);
      onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <div className="bg-[#121212] border border-white/10 p-5 mb-5" data-testid="add-members-panel">
      <div className="flex items-center justify-between mb-3">
        <p className="font-bold uppercase tracking-widest text-xs">Add Athletes</p>
        <button onClick={onClose} className="text-white/40 hover:text-white"><X size={16} /></button>
      </div>
      <div className="relative mb-3">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
        <input data-testid="add-members-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search athletes…" className={`${tInput} pl-9`} />
      </div>
      {loading ? <div className="flex justify-center py-8 text-white/40"><Loader2 className="animate-spin" /></div>
        : available.length === 0 ? <p className="text-white/40 text-sm py-4">No available athletes to add.</p>
        : (
          <div className="max-h-64 overflow-y-auto flex flex-wrap gap-1.5 mb-4">
            {available.map((a) => (
              <button key={a.id} data-testid={`pick-athlete-${a.id}`} onClick={() => toggle(a.id)}
                className={`px-2.5 py-1 text-xs border transition-colors ${sel.includes(a.id) ? "bg-white text-black border-white" : "border-white/15 text-white/50 hover:border-white/40"}`}>
                {a.name}{a.gender === "F" ? " (F)" : " (M)"}
              </button>
            ))}
          </div>
        )}
      <button data-testid="confirm-add-members" onClick={add} disabled={!sel.length || saving} className={tBtn}>{saving ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />} Add {sel.length || ""}</button>
    </div>
  );
}

function GroupDetail({ orgId, canWrite, groupId, coaches, onBack, onChanged }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [group, setGroup] = useState(null);
  const [dash, setDash] = useState(null);
  const [roster, setRoster] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [editing, setEditing] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [g, d, r, h] = await Promise.all([
        api.get(`/track/groups/${groupId}`),
        api.get(`/track/groups/${groupId}/dashboard`),
        api.get(`/track/groups/${groupId}/roster`),
        api.get(`/track/groups/${groupId}/history`),
      ]);
      setGroup(g.data); setDash(d.data); setRoster(r.data); setHistory(h.data);
    } catch { toast.error("Failed to load group."); } finally { setLoading(false); }
  }, [groupId]);
  useEffect(() => { load(); }, [load]);

  const currentIds = roster.map((a) => a.id);

  const removeMember = async (a) => {
    if (!window.confirm(`Remove ${a.name} from this group? Their athlete profile and history are kept.`)) return;
    try { await api.post(`/track/groups/${groupId}/members/remove`, { athlete_ids: [a.id] }); toast.success("Removed."); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const duplicate = async () => {
    try { await api.post(`/track/groups/${groupId}/duplicate`); toast.success("Group duplicated."); onChanged(); onBack(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const toggleArchive = async () => {
    const next = group.status === "archived" ? "active" : "archived";
    try { await api.patch(`/track/groups/${groupId}`, { status: next }); toast.success(next === "archived" ? "Archived." : "Restored."); load(); onChanged(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const del = async () => {
    if (!window.confirm("Permanently delete this group? Membership records are removed but athletes are kept. This cannot be undone.")) return;
    try { await api.delete(`/track/groups/${groupId}`); toast.success("Group deleted."); onChanged(); onBack(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const exportCsv = async () => {
    try {
      const res = await api.get(`/track/groups/${groupId}/roster.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${group.name}_roster.csv`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed."); }
  };

  if (loading || !group) return <div className="flex justify-center py-24 text-white/40"><Loader2 className="animate-spin" /></div>;

  const TABS = [
    { key: "overview", label: "Overview", icon: LayoutDashboard },
    { key: "roster", label: "Roster", icon: Users2 },
    { key: "history", label: "History", icon: History },
  ];

  return (
    <div data-testid="track-group-detail">
      <button data-testid="group-back" onClick={onBack} className="inline-flex items-center gap-2 text-white/50 hover:text-white text-sm mb-5 transition-colors"><ArrowLeft size={15} /> Back to groups</button>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-display font-black uppercase tracking-tighter text-4xl">{group.name}</h1>
            {group.status === "archived" && <span className="font-mono-accent text-[10px] tracking-[0.2em] uppercase border border-white/15 px-2 py-1 text-white/50">Archived</span>}
          </div>
          <p className="text-white/40 text-sm mt-1">{[group.level, group.sport, group.event_category].filter(Boolean).join(" · ") || "No details"}</p>
        </div>
        {canWrite && (
          <div className="flex gap-2 flex-wrap">
            <button data-testid="group-edit" onClick={() => setEditing((v) => !v)} className={tBtnGhost}><Pencil size={14} /> Edit</button>
            <button data-testid="group-duplicate" onClick={duplicate} className={tBtnGhost}><Copy size={14} /> Duplicate</button>
            <button data-testid="group-export" onClick={exportCsv} className={tBtnGhost}><Download size={14} /> Export</button>
            <button data-testid="group-archive" onClick={toggleArchive} className={tBtnGhost}>{group.status === "archived" ? <><ArchiveRestore size={14} /> Restore</> : <><Archive size={14} /> Archive</>}</button>
            {isAdmin && <button data-testid="group-delete" onClick={del} className="inline-flex items-center gap-2 border border-red-500/40 text-red-400 font-bold uppercase tracking-widest text-xs px-4 py-2.5 hover:bg-red-500 hover:text-white transition-colors"><Trash2 size={14} /> Delete</button>}
          </div>
        )}
      </div>

      {editing && canWrite && <div className="mt-4"><GroupForm orgId={orgId} initial={group} coaches={coaches} onCancel={() => setEditing(false)} onSaved={() => { setEditing(false); load(); onChanged(); }} /></div>}

      <div className="flex gap-1 border-b border-white/10 mt-6 mb-6" data-testid="group-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          return <button key={t.key} data-testid={`group-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 -mb-px transition-colors ${tab === t.key ? "border-white text-white font-bold" : "border-transparent text-white/45 hover:text-white/80"}`}><Icon size={15} /> {t.label}</button>;
        })}
      </div>

      {tab === "overview" && dash && (
        <div data-testid="group-overview">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-6">
            <div className="bg-[#0A0A0A] p-5"><p className="font-display font-black text-3xl leading-none">{dash.athlete_count}</p><p className="text-white/40 text-[10px] uppercase tracking-widest mt-1">Athletes</p></div>
            <div className="bg-[#0A0A0A] p-5"><p className="font-display font-black text-3xl leading-none">{dash.male}</p><p className="text-white/40 text-[10px] uppercase tracking-widest mt-1">Male</p></div>
            <div className="bg-[#0A0A0A] p-5"><p className="font-display font-black text-3xl leading-none">{dash.female}</p><p className="text-white/40 text-[10px] uppercase tracking-widest mt-1">Female</p></div>
            <div className="bg-[#0A0A0A] p-5"><p className="font-display font-black text-3xl leading-none">{group.capacity ?? "—"}</p><p className="text-white/40 text-[10px] uppercase tracking-widest mt-1">Capacity</p></div>
          </div>
          {group.coaches?.length > 0 && (
            <div className="mb-6"><p className={tLabel}>Coaches</p><div className="flex flex-wrap gap-2">{group.coaches.map((c) => <span key={c.id} className="font-mono-accent text-[11px] tracking-[0.1em] uppercase border border-white/15 px-2.5 py-1 text-white/70">{c.name}</span>)}</div></div>
          )}
          {group.description && <p className="text-white/60 text-sm mb-6 max-w-2xl">{group.description}</p>}
          <p className={tLabel}>Recent Results</p>
          {dash.recent_results.length === 0 ? <p className="text-white/40 text-sm">No results recorded for this group's athletes yet.</p>
            : <div className="border border-white/10 bg-[#121212] divide-y divide-white/5">{dash.recent_results.map((r, i) => (
                <div key={`${r.athlete_name}-${r.event_code}-${r.display}-${i}`} className="flex items-center justify-between px-4 py-2.5 text-sm"><span className="font-bold">{r.athlete_name}</span><span className="text-white/60">{r.event_code} · {r.display}</span></div>
              ))}</div>}
        </div>
      )}

      {tab === "roster" && (
        <div data-testid="group-roster">
          {canWrite && <div className="mb-4"><button data-testid="group-add-members-btn" onClick={() => setShowAdd((v) => !v)} className={tBtn}><UserPlus size={14} /> Add Athletes</button></div>}
          {showAdd && canWrite && <AddMembersPanel orgId={orgId} groupId={groupId} currentIds={currentIds} onDone={() => { setShowAdd(false); load(); }} onClose={() => setShowAdd(false)} />}
          {roster.length === 0 ? <div className="border border-white/10 bg-[#121212] p-6 text-white/40 text-sm" data-testid="group-roster-empty">No athletes in this group yet.</div>
            : (
              <div className="border border-white/10 bg-[#121212] overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-white/10 text-white/40">{["Name", "Gender", "Grad", "Latest Result", "actions"].map((h) => <th key={h} className="text-left font-mono-accent text-[10px] tracking-[0.2em] uppercase px-4 py-3">{h === "actions" ? "" : h}</th>)}</tr></thead>
                  <tbody>
                    {roster.map((a) => (
                      <tr key={a.id} data-testid={`group-member-${a.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                        <td className="px-4 py-3 font-bold">{a.name}</td>
                        <td className="px-4 py-3 text-white/60">{a.gender === "F" ? "Female" : "Male"}</td>
                        <td className="px-4 py-3 text-white/60">{a.grad_year || "—"}</td>
                        <td className="px-4 py-3 text-white/60">{a.latest_result ? `${a.latest_result.event_code} · ${a.latest_result.display}` : "—"}</td>
                        <td className="px-4 py-3 text-right">{canWrite && <button data-testid={`remove-member-${a.id}`} onClick={() => removeMember(a)} className="text-white/25 hover:text-red-400 transition-colors" title="Remove from group"><UserMinus size={15} /></button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </div>
      )}

      {tab === "history" && (
        <div data-testid="group-history">
          {history.length === 0 ? <p className="text-white/40 text-sm">No activity recorded yet.</p>
            : <div className="border border-white/10 bg-[#121212] divide-y divide-white/5">{history.map((h, i) => (
                <div key={`${h.action}-${h.at || ""}-${i}`} className="flex items-center justify-between px-4 py-2.5 text-sm flex-wrap gap-1">
                  <div className="flex items-center gap-2"><ClipboardList size={14} className="text-white/30" /><span>{HISTORY_LABELS[h.action] || h.action}{h.notes ? <span className="text-white/40"> — {h.notes}</span> : ""}</span></div>
                  <span className="text-white/30 text-xs">{h.by}{h.at ? ` · ${new Date(h.at).toLocaleDateString()}` : ""}</span>
                </div>
              ))}</div>}
        </div>
      )}
    </div>
  );
}

export default function TrackGroups({ orgId, canWrite }) {
  const [selected, setSelected] = useState(null);
  const [coaches, setCoaches] = useState([]);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    if (!orgId) return;
    api.get(`/track/group-coaches?organization_id=${orgId}`).then(({ data }) => setCoaches(data)).catch(() => setCoaches([]));
  }, [orgId]);

  if (selected)
    return <GroupDetail orgId={orgId} canWrite={canWrite} groupId={selected} coaches={coaches} onBack={() => setSelected(null)} onChanged={() => setRefresh((r) => r + 1)} />;
  return <GroupsList key={refresh} orgId={orgId} canWrite={canWrite} coaches={coaches} onOpen={setSelected} />;
}
